import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { installationService } from '../services/api';
import { Loader2 } from 'lucide-react';

// Map component for location picker
const LocationPicker = ({ onSelect }) => {
  const [position, setPosition] = useState([35.6892, 51.3890]);

  // Simple location input instead of Leaflet (to avoid dependency issues)
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            عرض جغرافیایی (Latitude)
          </label>
          <input
            type="text"
            placeholder="مثال: 35.6892"
            className="w-full border border-gray-300 rounded-lg px-4 py-3"
            onChange={(e) => {
              const lat = parseFloat(e.target.value);
              setPosition([lat, position[1]]);
              if (!isNaN(lat)) onSelect(lat, position[1]);
            }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            طول جغرافیایی (Longitude)
          </label>
          <input
            type="text"
            placeholder="مثال: 51.3890"
            className="w-full border border-gray-300 rounded-lg px-4 py-3"
            onChange={(e) => {
              const lng = parseFloat(e.target.value);
              setPosition([position[0], lng]);
              if (!isNaN(lng)) onSelect(position[0], lng);
            }}
          />
        </div>
      </div>
      <p className="text-xs text-gray-500">
        می‌توانید مختصات را از Google Maps دریافت کنید
      </p>
    </div>
  );
};

export const InstallationRequestForm = () => {
  const [step, setStep] = useState(1);
  const [coords, setCoords] = useState({ lat: 35.6892, lng: 51.3890 });
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm();

  const onSubmit = async (data) => {
    try {
      const requestData = {
        ...data,
        latitude: coords.lat,
        longitude: coords.lng,
      };
      
      await installationService.createRequest(requestData);
      toast.success('درخواست نصب با موفقیت ثبت شد!');
      setStep(1);
    } catch (error) {
      toast.error('خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.');
    }
  };

  const steps = [
    { id: 1, title: 'اطلاعات پایه' },
    { id: 2, title: 'موقعیت مکانی' },
    { id: 3, title: 'جزئیات فنی' },
    { id: 4, title: 'تایید نهایی' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
      {/* Steps Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((s, index) => (
          <div key={s.id} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= s.id ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {s.id}
            </div>
            <span className={`hidden md:block mr-2 text-sm ${
              step >= s.id ? 'text-blue-700 font-medium' : 'text-gray-400'
            }`}>
              {s.title}
            </span>
            {index < steps.length - 1 && (
              <div className={`w-12 md:w-24 h-1 mx-2 ${
                step > s.id ? 'bg-blue-600' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">اطلاعات پایه</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  آدرس دقیق *
                </label>
                <textarea
                  {...register('address', { required: 'آدرس الزامی است' })}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="آدرس کامل محل نصب را وارد کنید..."
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-500">{errors.address.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نوع سقف *
                </label>
                <select
                  {...register('roof_type', { required: 'نوع سقف الزامی است' })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">انتخاب کنید</option>
                  <option value="flat">سقف مسطح</option>
                  <option value="sloped">سقف شیبدار</option>
                  <option value="ground">فضای باز / زمین</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  مساحت تقریبی (متر مربع) *
                </label>
                <input
                  type="number"
                  {...register('roof_area', { required: true, min: 1 })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  تاریخ پیشنهادی نصب *
                </label>
                <input
                  type="date"
                  {...register('preferred_date', { required: true })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  میانگین قبض برق ماهانه (تومان) *
                </label>
                <input
                  type="number"
                  {...register('monthly_bill', { required: true })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                مرحله بعد
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Location */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">انتخاب موقعیت مکانی</h2>
            <p className="text-gray-600 mb-4">مختصات جغرافیایی محل نصب را وارد کنید.</p>
            
            <LocationPicker onSelect={(lat, lng) => setCoords({ lat, lng })} />

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">موقعیت انتخاب شده:</p>
              <p className="font-mono text-sm">Lat: {coords.lat}, Lng: {coords.lng}</p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-50"
              >
                مرحله قبل
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700"
              >
                مرحله بعد
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Technical Details */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">جزئیات تکمیلی</h2>
            
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  توضیحات تکمیلی
                </label>
                <textarea
                  {...register('description')}
                  rows={5}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                  placeholder="هرگونه اطلاعات تکمیلی درباره محل نصب، محدودیت‌ها یا نیازهای خاص..."
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-50"
              >
                مرحله قبل
              </button>
              <button
                type="button"
                onClick={() => setStep(4)}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700"
              >
                مرحله بعد
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">تایید نهایی</h2>
            
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">موقعیت:</span>
                <span className="font-medium">{coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}</span>
              </div>
              <p className="text-sm text-gray-500">
                با ثبت این درخواست، اطلاعات شما برای نصاب‌های مجاز ارسال خواهد شد.
              </p>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-50"
              >
                مرحله قبل
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    در حال ارسال...
                  </>
                ) : (
                  'ثبت درخواست نصب'
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default InstallationRequestForm;